import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Check, TrendingUp, Zap, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { StripeProduct, StripePrice } from "@/services/payments";

// Icon mapping for different plan types based on metadata
const planIcons = {
  intern: Sparkles,
  junior: TrendingUp,
  senior: Zap,
} as const;

// Helper function to get the correct icon based on metadata or plan type
const getPlanIcon = (product: StripeProduct) => {
  if (product.metadata.icon) {
    const iconName = product.metadata.icon.toLowerCase();
    if (iconName in planIcons) {
      return planIcons[iconName as keyof typeof planIcons];
    }
  }

  const planId = product.metadata.plan_id || product.name.toLowerCase();
  return planIcons[planId as keyof typeof planIcons] || Sparkles;
};

// Format price for display (convert from cents to dollars)
const formatPrice = (unitAmount: number): number => {
  return Math.round(unitAmount / 100);
};

// Check if plan is popular (can be set via metadata)
const isPlanPopular = (product: StripeProduct): boolean => {
  return (
    product.metadata.isPopular === "true" ||
    product.metadata.popular === "true" ||
    product.metadata.plan_id === "junior"
  );
};

interface PricingPlansGridProps {
  products: StripeProduct[];
  prices: StripePrice[];
  mode: "paywall" | "billing";
  onPlanAction: (productId: string, isAnnual: boolean) => void;
  loading?: string | null;
  className?: string;
}

export function PricingPlansGrid({
  products,
  prices,
  mode,
  onPlanAction,
  loading,
  className,
}: PricingPlansGridProps) {
  const [isAnnual, setIsAnnual] = useState<boolean>(mode === "paywall");

  // Get prices for a specific product
  const getProductPrices = (productId: string) => {
    return prices.filter((price) => price.product_id === productId);
  };

  // Get monthly and annual prices for a product
  const getPricesForProduct = (productId: string) => {
    const productPrices = getProductPrices(productId);
    const monthlyPrice = productPrices.find(
      (price) => price.interval === "month",
    );
    const annualPrice = productPrices.find(
      (price) => price.interval === "year",
    );
    return { monthlyPrice, annualPrice };
  };

  // Get display price based on billing type
  const getDisplayPrice = (productId: string) => {
    const { monthlyPrice, annualPrice } = getPricesForProduct(productId);

    if (isAnnual && annualPrice) {
      return formatPrice(annualPrice.unit_amount / 12); // Monthly equivalent for annual
    } else if (monthlyPrice) {
      return formatPrice(monthlyPrice.unit_amount);
    }

    return 0;
  };

  const getActionButtonText = (mode: "paywall" | "billing") => {
    return (
      <div className="flex items-center justify-center gap-2">
        <span className="text-lg">🚀</span>
        <span>{mode === "paywall" ? "Start Free Trial" : "Subscribe"}</span>
      </div>
    );
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div className="flex items-center space-x-4 bg-muted p-2 rounded-lg">
          <Label
            htmlFor="billing-toggle"
            className={cn("cursor-pointer ml-2", !isAnnual && "font-medium")}
          >
            Monthly billing
          </Label>
          <Switch
            id="billing-toggle"
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
          />
          <Label
            htmlFor="billing-toggle"
            className={cn(
              "cursor-pointer flex items-center gap-2",
              isAnnual && "font-medium",
            )}
          >
            Annual billing
            <Badge
              variant="secondary"
              className="text-xs bg-green-100 text-green-700 hover:bg-green-100"
            >
              2 months free
            </Badge>
          </Label>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {products
          .sort((a, b) => a.product_order - b.product_order)
          .map((product) => {
            const planId =
              product.metadata.plan_id || product.name.toLowerCase();
            const Icon = getPlanIcon(product);
            const isPopular = isPlanPopular(product);
            const features = (product.marketing_features as string[]) || [];
            const displayPrice = getDisplayPrice(product.product_id);
            return (
              <Card
                key={product.product_id}
                className={cn(
                  "relative p-6 transition-all duration-200 hover:shadow-lg flex flex-col h-full",
                  isPopular && "ring-2 ring-gray-900",
                )}
              >
                {isPopular && (
                  <Badge className="absolute -top-3 left-6 bg-gray-900 text-white px-3 py-1">
                    Most Popular
                  </Badge>
                )}

                <CardContent className="p-0 flex flex-col h-full">
                  {/* Plan Header */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
                      <Icon className="h-6 w-6 text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-gray-900">
                        {product.name}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {product.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-gray-900">
                        ${displayPrice}
                      </div>
                      <div className="text-sm text-gray-500">per month</div>
                      {isAnnual && (
                        <div className="text-xs text-gray-400">
                          Billed annually
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Features - flex-grow to push button to bottom */}
                  <ul className="space-y-3 flex-grow mb-6">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Action Button - Always at bottom */}
                  <Button
                    className={cn(
                      "w-full py-3 text-base font-medium mt-auto transition-all duration-200",
                      isPopular
                        ? "bg-gray-900 hover:bg-gray-800 text-white"
                        : "bg-white border-2 border-gray-200 hover:border-gray-900 hover:bg-gray-50 text-gray-900",
                    )}
                    onClick={() => onPlanAction(product.product_id, isAnnual)}
                    disabled={loading === planId}
                  >
                    {loading === planId
                      ? mode === "paywall"
                        ? "Starting Trial..."
                        : "Subscribing..."
                      : getActionButtonText(mode)}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
      </div>
    </div>
  );
}
